import { useCallback, useEffect, useState } from 'react';
import {
  getMyHtxMembership,
  requestJoinHtx,
  leaveMyHtx,
  cancelHtxJoinRequest,
  listHtx,
  respondToHtxInvite,
  type MyHtxMembership,
  type BackendHtx,
} from '@/api/ekyc';
import { ApiError } from '@/api/client';
import { showToast } from '@/components/base/Toast';
import { confirmModal } from '@/components/base/ConfirmModal';

/**
 * The farmer's cooperative membership, on their own account screen.
 *
 * None of this was reachable from here: the account tab said nothing about which
 * HTX the farmer belonged to, an invitation could only be answered from a banner on
 * the dashboard, and there was no way at all to leave or to ask to join one —
 * reassignFarmerHtx is ADMIN/OPS and htx-invite is HTX-initiated. Meanwhile the HTX
 * side has had its "Yêu cầu gia nhập" queue all along, so the relationship was only
 * ever visible and actionable from one end.
 */
export function MyHtxSection() {
  const [state, setState] = useState<MyHtxMembership | null>(null);
  const [loading, setLoading] = useState(true);
  const [htxList, setHtxList] = useState<BackendHtx[]>([]);
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getMyHtxMembership()
      .then(setState)
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  // Only needed for the join picker, so it is fetched only when there is no
  // cooperative to show, or the farmer is redirecting a pending request.
  useEffect(() => {
    if (state && (!state.htxId || changing) && htxList.length === 0) {
      listHtx().then(setHtxList).catch(() => setHtxList([]));
    }
  }, [state, changing, htxList.length]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      showToast(ok, 'success');
      setChanging(false);
      setPicked('');
      load();
    } catch (e) {
      // The server names every blocker at once for leaving; pass it through
      // verbatim rather than replacing it with something vaguer.
      showToast(e instanceof ApiError ? e.message : 'Không thực hiện được, vui lòng thử lại', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">Đang tải thông tin hợp tác xã...</div>;
  }
  if (!state) return null;

  const pendingInvite = state.membershipStatus === 'PENDING' && state.membershipInitiatedBy === 'HTX';
  const pendingOwnRequest = state.membershipStatus === 'PENDING' && state.membershipInitiatedBy === 'FARMER';
  const isMember = state.membershipStatus === 'ACTIVE' && !!state.htxId;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="my-htx-section">
      <h3 className="text-sm font-bold text-slate-900">Hợp tác xã</h3>

      {state.htxId ? (
        <div className="mt-2">
          <p className="text-sm font-semibold text-slate-900" data-testid="my-htx-name">{state.htxName ?? 'Hợp tác xã'}</p>
          <p className="text-[11px] text-slate-500">{state.htxCode ?? ''}</p>
          <span
            data-testid="my-htx-status"
            className={`mt-2 inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold ${
              isMember
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
            }`}
          >
            {isMember ? 'Đã là thành viên' : pendingInvite ? 'HTX mời bạn gia nhập' : 'Đang chờ HTX xác nhận'}
          </span>
        </div>
      ) : (
        <p className="mt-2 text-sm text-slate-500" data-testid="my-htx-none">
          Bạn chưa thuộc hợp tác xã nào. Gia nhập một hợp tác xã để được cấp hạn mức, vay vốn và nhận vật tư.
        </p>
      )}

      {state.membershipRejectedReason && (
        <p className="mt-2 rounded-lg bg-red-50 px-2.5 py-2 text-[11px] text-red-700">
          Lý do từ chối: {state.membershipRejectedReason}
        </p>
      )}

      {/* An invitation could previously only be answered from the dashboard banner. */}
      {pendingInvite && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={busy}
            data-testid="accept-invite"
            onClick={() => act(() => respondToHtxInvite(state.farmerId, 'ACCEPT'), 'Đã gia nhập hợp tác xã')}
            className="flex-1 h-11 rounded-xl bg-agrida-600 text-white text-xs font-bold disabled:opacity-50"
          >
            Đồng ý gia nhập
          </button>
          <button
            type="button"
            disabled={busy}
            data-testid="decline-invite"
            onClick={() => act(() => respondToHtxInvite(state.farmerId, 'DECLINE'), 'Đã từ chối lời mời')}
            className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold disabled:opacity-50"
          >
            Từ chối
          </button>
        </div>
      )}

      {pendingOwnRequest && (
        <>
          <p className="mt-3 text-[11px] text-slate-500">
            Yêu cầu gia nhập của bạn đang chờ hợp tác xã xác nhận. Trong lúc chờ, bạn có thể đổi sang hợp tác xã khác hoặc hủy yêu cầu.
          </p>
          {!changing && (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                disabled={busy}
                data-testid="change-join-request"
                onClick={() => setChanging(true)}
                className="flex-1 h-11 rounded-xl border border-agrida-300 bg-agrida-50 text-agrida-700 text-xs font-bold disabled:opacity-50"
              >
                Đổi hợp tác xã
              </button>
              <button
                type="button"
                disabled={busy}
                data-testid="cancel-join-request"
                onClick={async () => {
                  const yes = await confirmModal({
                    title: 'Hủy yêu cầu gia nhập?',
                    message: `Yêu cầu gửi tới ${state.htxName ?? 'hợp tác xã này'} sẽ bị hủy. Bạn có thể gửi yêu cầu mới bất cứ lúc nào.`,
                    confirmText: 'Hủy yêu cầu',
                    variant: 'danger',
                  });
                  if (yes) void act(() => cancelHtxJoinRequest(), 'Đã hủy yêu cầu gia nhập');
                }}
                className="flex-1 h-11 rounded-xl border border-red-200 text-red-600 text-xs font-bold disabled:opacity-50"
              >
                Hủy yêu cầu
              </button>
            </div>
          )}
        </>
      )}

      {isMember && (
        <button
          type="button"
          disabled={busy}
          data-testid="leave-htx"
          onClick={async () => {
            // Leaving cuts the farmer off from limits, loans and inputs, so it asks
            // first — the same ConfirmModal the rest of the app uses.
            const yes = await confirmModal({
              title: 'Rời hợp tác xã?',
              message: `Bạn sẽ không còn nhận hạn mức, vay vốn hay vật tư từ ${state.htxName ?? 'hợp tác xã này'}. Bạn có thể gửi yêu cầu gia nhập lại sau.`,
              confirmText: 'Rời hợp tác xã',
              variant: 'danger',
            });
            if (yes) void act(() => leaveMyHtx(), 'Đã rời hợp tác xã');
          }}
          className="mt-3 h-11 w-full rounded-xl border border-red-200 text-red-600 text-xs font-bold disabled:opacity-50"
        >
          Rời hợp tác xã
        </button>
      )}

      {(!state.htxId || (pendingOwnRequest && changing)) && (
        <div className="mt-3 space-y-2">
          <select
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            data-testid="join-htx-select"
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          >
            <option value="">— Chọn hợp tác xã —</option>
            {htxList
              .filter((h) => h._id !== state.htxId)
              .map((h) => (
                <option key={h._id} value={h._id}>{h.name} ({h.code})</option>
              ))}
          </select>
          {htxList.length === 0 && (
            <p className="text-[11px] text-amber-600">Chưa tải được danh sách hợp tác xã. Vui lòng thử lại sau.</p>
          )}
          <button
            type="button"
            disabled={busy || !picked}
            data-testid="join-htx"
            onClick={() =>
              act(
                () => requestJoinHtx(picked),
                changing ? 'Đã chuyển yêu cầu sang hợp tác xã mới, đang chờ xác nhận' : 'Đã gửi yêu cầu gia nhập, đang chờ hợp tác xã xác nhận',
              )
            }
            className="h-11 w-full rounded-xl bg-agrida-600 text-white text-xs font-bold disabled:opacity-50"
          >
            {changing ? 'Chuyển yêu cầu sang hợp tác xã này' : 'Gửi yêu cầu gia nhập'}
          </button>
          {changing && (
            <button
              type="button"
              disabled={busy}
              onClick={() => { setChanging(false); setPicked(''); }}
              className="h-10 w-full rounded-xl text-slate-500 text-xs font-semibold"
            >
              Giữ yêu cầu hiện tại
            </button>
          )}
        </div>
      )}

    </section>
  );
}
